import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import AlarmSetpointEditorUtils, { SetPointAdminMode, SetpointFormType, SetpointUsageActions, StepEnum, usageActionItems } from "./AlarmSetpointEditorUtils";
import { SetPointPrecedenceTypeEnum, defaultEditSetpoint } from "../../services/setpoint-administration-service/Models";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import StepMenu, { StepDefinition } from "../step-menu/StepMenu";
import { StepMenuEditModeEnum, useStepMenuState } from "../../components/step-menu/StepMenuState";
import Form from "../forms/form/Form";
import UnsavedChangesPrompt from "../unsaved-changes-prompt/UnsavedChangesPrompt";
import { isMobile, isTablet } from "react-device-detect";
import MobileStepMenu from "../step-menu/mobile/MobileStepMenu";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { useGetSetpoint } from "../../queries/alarm-setpoint-admin/UseGetSetpoint";
import AlarmSetpointProperties from "./alarm-setpoint-properties/AlarmSetpointProperties";
import AlarmSetpointUsage, { AlarmSetpointUsageType } from "./alarm-setpoint-usage/AlarmSetpointUsage";
import { useGetAvailableUsageObjects } from "../../queries/alarm-setpoint-admin/UseGetAvailableUsageObjects";
import { SetPointTypeEnum } from "../../common/enums/SetpointTypeEnum";
import LoadingZone from "../loading-zone/LoadingZone";
import { ViewOptionStateEnum } from "../contextual-app-bar/ViewOptionStateEnum";
import { ActionMenuItem, BaseActions } from "../action-menu/ActionMenuItem";
import { useSetpointReferenceData } from "../../queries/alarm-setpoint-admin/UseSetpointReferenceData";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useGroup } from "../../queries/groups/UseGroup";
import AlarmSetpointNotifications, { AlarmSetpointNotificationsType } from "./alarm-setpoint-notifications/AlarmSetpointNotifications";
import { AlarmSetpointNotificationTabEnum, NotificationTypeEnum, useAlarmSetpointNotificationState } from "./alarm-setpoint-notifications/AlarmSetpointNotificationState";
import { useAlarmSetpointUsageState } from "./alarm-setpoint-usage/AlarmSetpointUsageState";
import AlarmSetpointMessaging from "./alarm-setpoint-messaging/AlarmSetpointMessaging";
import { useAlarmSetpointMessagingState } from "./alarm-setpoint-messaging/AlarmSetpointMessagingState";
import AlarmSetpointSummary from "./alarm-setpoint-summary/AlarmSetpointSummary";
import { useCustomerAttribute } from "../../queries/attributes/UseCustomerAttribute";
import { useSaveSetpoint } from "../../queries/alarm-setpoint-admin/UseSaveSetpoint";
import SetpointAdministrationDataService from "../../services/setpoint-administration-service/SetpointAdministrationService";
import { RestError } from "../../services/rest-service/RestService";
import { AppNotificationTypeEnum } from "../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../contexts/app-notification-context/UseAppNotificationContext";
// import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useObjectViewState } from "../../queries/object-view-state/UseObjectViewState";
import { ITagViewState } from "../../services/state-data-service/Models";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { FormRecipientActions, recipientActionItems } from "../form-recipient-list/FormRecipientListUtils";
import { FormNotificationListsActions, notificationListActionItems } from "../form-notification-lists/FormNotificationListsUtils";
import { useGetAlarmSchemes } from "../../queries/alarm-scheme-admin/UseGetAlarmSchemes";
import { DataTypeEnum } from "../../common/enums/DataTypeEnum";

export type AlarmSetpointEditorCloseType = {
    reason: "cancel" | "save";
    setpointID: number | undefined;
};

export type AlarmSetpointEditorProps = {
    groupID: number,
    setpointID: number | undefined;
    singleTagMode?: boolean;
    copy?: boolean;
    onClose: ({ reason }: AlarmSetpointEditorCloseType) => void;
    objectName?: string;
    tagName?: string;
    tagID?: number;
};

export default function AlarmSetpointEditor({
    groupID,
    setpointID,
    singleTagMode,
    copy,
    onClose,
    objectName,
    tagName,
    tagID
}: AlarmSetpointEditorProps): JSX.Element {

    const [updating, setUpdating] = useState(false);
    const isPhone = isMobile && !isTablet;
    // const routeContext = useRouteContext();
    const [userState] = useUserContext();
    const [,setAppNotificationState] = useAppNotificationContext();
    const { confirm
        , acknowledge 
    } = useConfirmation();
    const { t } = useTranslation();
    const [viewOptionState, setViewOptionState] = useState(ViewOptionStateEnum.Default);
    const [openSideSheet, setOpenSideSheet] = useState(isPhone ? false: true);
    const setpointNotificationState = useAlarmSetpointNotificationState();
    const setpointUsageState = useAlarmSetpointUsageState();

    const {setObjectFilter, setTagFilter} = setpointUsageState;

    const setpointMessagingState = useAlarmSetpointMessagingState();

    const mode = (setpointID ?? 0) <= 0 || copy ? 
        SetPointAdminMode.Add : 
        SetPointAdminMode.Edit;

    const setpointUsageRef = useRef<AlarmSetpointUsageType>(null);
    const setpointNotificationsRef = useRef<AlarmSetpointNotificationsType>(null);

    const { data: objectViewState } = useQueryNotifications(useObjectViewState)({
        customerID: userState.customer?.id as number,
        // groupID: routeContext.groupID as number,
        // contextObject: routeContext.contextObject,
        groupID: groupID as number,
        contextObject: {
            objectType: eLynxObjectTypeEnum.Tag,
            id: tagID as number
        },
        offline: false,
        // enabled: routeContext.contextObject?.objectType === eLynxObjectTypeEnum.Tag
        enabled: (tagID ?? 0) > 0
    });


    const {data: group } = useQueryNotifications(useGroup)({
        customerID: userState.customer?.id as number,
        groupID
    });


    const {data, status, refetch: refetchSetpoint } = useQueryNotifications(useGetSetpoint)({
        groupID,
        organizationID: group?.organizationID as number,
        setpointID: setpointID,
        enabled: group !== undefined
    });

    //Bug 54174: Create Setpoint from Tag Details UOM - DOC
    //Bug #54109: DOC: Incorrect UoM or unit conversion in CID 3082
    //const uomID: number | undefined = /*(objectViewState as ITagViewState)?.uomID ??*/ data?.unitOfMeasureID;
    const uomID: number | undefined = (data?.setPointID ?? 0) === 0 ? (objectViewState as ITagViewState)?.uomID : data?.unitOfMeasureID ;
    const setpointDataType: DataTypeEnum | undefined = (objectViewState as ITagViewState)?.dataType ?? data?.dataType;

    const {data: allowVoiceAlarmAttribute } = useQueryNotifications(useCustomerAttribute)({
        groupID ,
        name: 'allow voice alarm',
        enabled: group !== undefined
    });

    const allowVoiceAlarm = allowVoiceAlarmAttribute?.attributeValue.toLowerCase() === 'true';

    const form = useForm<SetpointFormType>({
        //mode: "all",
        mode: 'onChange',
        defaultValues: {...defaultEditSetpoint,
            // devices: [],
            // wells: [],
            // tanks:[],
            // tankBatteries: [],
            deviceTags: [],
            wellTags: [],
            tankTags: [],
            tankBatteryTags: [],
            userRecipients: []
        }
    });

    const {
        getValues,
        watch,
        formState: {
            dirtyFields,
            //isDirty,
            //isValid,
            //defaultValues
            //touchedFields
        },
        reset,
        //setValue,
        control,
        handleSubmit,
    } = form;
    
    const hasChanges = Object.keys(dirtyFields).length > 0 || (copy ?? false);

    const setpointFormData = useWatch({ control: control }) as SetpointFormType;

    const [setPointType,
        unitOfMeasureID,
        dataType,
        comparisonType,
        //devices,
        //tanks,
        //wells,
        //tankBatteries,
        isDiscrete,
        //setPointName
        ] = watch([
        'setPointType', 
        'unitOfMeasureID', 
        'dataType',
        'comparisonType',
        //'devices',
        //'tanks',
        //'wells',
        //'tankBatteries',
        'isDiscrete',
        //'setPointName'
    ]);

    const { data: usageObjects, status: usageStatus } = useQueryNotifications(useGetAvailableUsageObjects)({
        groupID,
        searchCriteria: {
            //tagObjectName: null,
            startGroupID: groupID,
            //deviceTypeID: null,
            isDiscrete: setPointType === SetPointPrecedenceTypeEnum.Field || isDiscrete, //
            // this.setPoint.isDiscrete,
            tagAssociationTypeIDs: [],
            unitOfMeasureID: (comparisonType !== SetPointTypeEnum.VariationPercent &&
                comparisonType !== SetPointTypeEnum.TimeComparison)
                ? unitOfMeasureID
                : undefined,
            //tagObjectTypeID: null,
            dataTypeID: dataType
        },
        enabled: groupID !== undefined && data !== undefined
    });


    const organizationID = setpointFormData.setPointID > 0 ? setpointFormData.organizationID : group?.organizationID as number;

    const {data: referenceData /*status: referenceStatus */, refetch: refetchReferenceData} = useQueryNotifications(useSetpointReferenceData)({
        groupID ,
        organizationID: organizationID,
        enabled: groupID !== undefined
    });


    const {refetch: refetchAlarmSchemes} = useQueryNotifications(useGetAlarmSchemes)({
        groupID ,
        organizationID: organizationID,
        enabled: groupID !== undefined
    });

    const { mutate: saveSetpoint/*, status*/ } = useQueryNotifications(useSaveSetpoint)({
        groupID: groupID,
        organizationID: group?.organizationID as number,
        onSuccess: async (data) => {
            acknowledge(t(mode === SetPointAdminMode.Add ? 
                            "setpointAdmin.successMessageAdded" : 
                            "setpointAdmin.successMessageUpdated", { 
                                name: data.setPointName 
                            }
                        ));

            await Promise.all([refetchSetpoint(), refetchReferenceData(), refetchAlarmSchemes()]);
            
            if (setpointFormData.originalTagCount > 0 && AlarmSetpointEditorUtils.setpointTagCount(setpointFormData) === 0) {

                const result = await confirm({
                    dialogTitle: t("setpointAdmin.noTagsRemaining"),
                    confirmationText: t("setpointAdmin.setpointDeleteConfirmation"),
                    yesButtonText: t("buttons.yes"),
                    noButtonText: t("buttons.no"),
                    showConfirmationText: true,
                    showYesButton: true,
                    showNoButton: true,
                    showCloseButton: true
                });

                if (result ==="yes") {
                    try {
                        await SetpointAdministrationDataService.deleteSetpoint(groupID, setpointFormData.setPointID);
                    } catch (error) {
                        const restError = error as RestError;
                        
                        setAppNotificationState({
                            notificationType: AppNotificationTypeEnum.Error,
                            show: true,
                            error: restError
                        });
            
                    }
                }
            }
            
            setUpdating(false);
            onClose({
                reason: "save",
                setpointID: setpointFormData.setPointID
            });
        },
        onError: () => {
            setUpdating(false);
        }
    });


    //const hasData = data && usageObjects;
    const propertiesValid = setpointFormData.setPointName !== '' && !isNaN(setpointFormData.value);
    const usageValid = AlarmSetpointEditorUtils.setpointTagCount(setpointFormData) > 0;

    const notificationsValid = setpointFormData.userRecipients.flatMap(o => o.recipients).filter(o => o.selected).length > 0 ||
                                setpointFormData.notificationLists.filter(o => o.selected).length > 0 ||
                                setpointFormData.alarmSchemes.filter(o => o.selected).length > 0;

    const actionsValid = setpointFormData.emailAlarmMessageSubject.trim().length !== 0 &&
                            setpointFormData.emailAlarmMessageBody.trim().length !== 0 &&
                            setpointFormData.emailClearMessageSubject.trim().length !== 0 &&
                            setpointFormData.emailClearMessageBody.trim().length !== 0;

    const stepDefinitions: StepDefinition[] = useMemo(() => {

        return [{
            key: StepEnum.Logic,
            title: t("setpointAdmin.steps.logic.title"),
            //disabled: !propertiesValid,
            valid: propertiesValid

        }, {
            key: StepEnum.Usage,
            title: t("setpointAdmin.steps.usage.title"),
            disabled: !propertiesValid,
            valid: usageValid
        },{
            key: StepEnum.Notifications,
            title: t("setpointAdmin.steps.notifications.title"),
                disabled: !propertiesValid,
                valid: notificationsValid
        },{
            key: StepEnum.Actions,
            title: t("setpointAdmin.steps.actions.title"),
                disabled: !propertiesValid,
                valid: actionsValid
        },{
            key: StepEnum.Summary,
            title: t("setpointAdmin.steps.summary.title"),
            disabled: !propertiesValid || !actionsValid,
        }];
    }, [
        t,
        propertiesValid,
        usageValid,
        notificationsValid,
        actionsValid

    ]);

    const {
        state: {
            landing,
            steps,
            activeStep,
            cancelButtonState,
            prevButtonState,
            nextButtonState,
            saveChangesButtonState,
        },
        setActiveStep,
        setNextButtonDisabled,
        setSaveChangesButtonLabel,
        setSaveChangesButtonDisabled,
        setSaveChangesButtonVisible,
    } = useStepMenuState(
        StepMenuEditModeEnum.Create, //@@ add or edit
        undefined,
        stepDefinitions
    );

    const updateButtonStates = useCallback(() => {

         switch (activeStep) {
            case StepEnum.Logic:
                setSaveChangesButtonLabel(t(mode === SetPointAdminMode.Add ? 
                        'setpointAdmin.buttons.create' : 
                        'setpointAdmin.buttons.save'));
                setNextButtonDisabled(!propertiesValid);
                break;

            case StepEnum.Usage:
                setNextButtonDisabled(false);
                break;

            case StepEnum.Notifications:
                setNextButtonDisabled(false);                
                break;

            case StepEnum.Actions:
                setNextButtonDisabled(false);                
                break;

            case StepEnum.Summary:
                setSaveChangesButtonVisible(true);
                setSaveChangesButtonDisabled(!hasChanges);
                break;

            default:
                break;
        }
    }, [
        t,
        mode,
        setNextButtonDisabled,
        setSaveChangesButtonLabel,
        setSaveChangesButtonDisabled,
        setSaveChangesButtonVisible,
        activeStep,
        propertiesValid,
        hasChanges
    ]);


    async function onCancelEdit() {

        if (!hasChanges || (await getUserExitConfirmation())) {
            onClose({ 
                reason: "cancel", 
                setpointID: setpointFormData.setPointID
            });
        }
    }

    function onCancelStep() {
        onCancelEdit();
    }

    function onStepChange(fromStep: number, toStep: number) {

        setActiveStep(toStep);
    }

    async function onSaveChanges() {

        const values = getValues();
        const evaluationChanges = AlarmSetpointEditorUtils.evaluationPropertyChanges(dirtyFields);
        const model = AlarmSetpointEditorUtils.mapToModel(values, evaluationChanges);

        if (mode === SetPointAdminMode.Edit && evaluationChanges &&
            model.tags.length > 1 &&
            !singleTagMode &&
            (values.notifyOnAlarm || values.notifyOnNormal)) {


                const result = await confirm({
                    dialogTitle: t("setpointAdmin.evaluationChanges.title"),
                    confirmationText: t("setpointAdmin.evaluationChanges.text", {
                        tagCount: model.tags.length
                    }),
                    yesButtonText: t("buttons.yesContinue"),
                    noButtonText: t("buttons.noCancel"),
                    showConfirmationText: true,
                    showYesButton: true,
                    showNoButton: true,
                    showCloseButton: true
                });

                if (result !=="yes") {
                    return;
                }
        }


        setUpdating(true);

        saveSetpoint(model);
    }

    function onExecuteAction(menuItem: ActionMenuItem) {

        switch(activeStep) {
            case StepEnum.Usage: {
                const action = menuItem.action as SetpointUsageActions | BaseActions;
                setpointUsageRef.current?.executeAction(action);
            }
                break;

            case StepEnum.Notifications: {
                const action = menuItem.action as FormRecipientActions | FormNotificationListsActions | BaseActions;
                setpointNotificationsRef.current?.executeAction(action);
            }
                break;

            default:
                break;
        }

    }

    async function getUserExitConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: true
        })) === "yes";
    }

    useEffect(() => {

        if (data && referenceData) {

            reset(AlarmSetpointEditorUtils.mapToFormValues(data, 
                    -1, 
                    //[], 
                    referenceData, 
                    allowVoiceAlarm, 
                    setpointDataType,
                    copy,
                    // routeContext?.tagID,
                    tagID,
                    uomID), {
                        keepDirtyValues: true
                    });
        }
    }, [
        data,
        referenceData,
        reset,
        allowVoiceAlarm,
        copy,
        tagID,
        uomID,
        setpointDataType
    ]);

    // SPR 09/04/2026 - Don't want to store all of the usage objects in the form data, just the tags that are selected.
    // Storing everything has a tremendous performance impact when there are a lot of tags because of the change tracking
    // built in to react-hook-form.
    // usageObjects can change based on uom and setpoint type so need to update seperately
    // useEffect(() => {

    //     const formValue = getValues();
    //     if (referenceData && usageObjects && usageObjects.length > 0) {
    //         reset(AlarmSetpointEditorUtils.mapToFormValues(formValue, 
    //                                         setpointFormData.unitClassification, 
    //                                         usageObjects,
    //                                         referenceData,
    //                                         allowVoiceAlarm,
    //                                         setpointDataType,
    //                                         false,
    //                                         // routeContext?.tagID,
    //                                         tagID,
    //                                         formValue.unitOfMeasureID,
    //                                         //uomID,
    //                                         true), {
    //                                             keepDirtyValues: true
    //                                         });
    //     }
    // }, [
    //     usageObjects,
    //     getValues,
    //     referenceData,
    //     setpointFormData.unitClassification,
    //     reset,
    //     allowVoiceAlarm,
    //     tagID,
    //     uomID,
    //     setpointDataType
    // ]);

    useEffect(() => {

         updateButtonStates();
    }, [
        setpointFormData, 
        updateButtonStates
    ]);

    useEffect(() => {
        if (isPhone && (activeStep === StepEnum.Usage || 
                activeStep === StepEnum.Notifications ||
                activeStep === StepEnum.Actions)) {
            setOpenSideSheet(false);
        }
    }, [
        isPhone,
        activeStep,
        setOpenSideSheet
    ]);

    useEffect(() => {

        if (objectName) {
            setObjectFilter(objectName)
        }

        if (tagName) {
            setTagFilter(tagName);
        }
    },[
        objectName,
        tagName,
        setObjectFilter,
        setTagFilter
    ]);

    return (
        <LoadingZone isLoading={status === 'loading' || usageStatus === 'loading' || updating}>
            
            {data && <Form form={form} sx={{ padding: 0 }}>

                {isPhone && 
                <MobileStepMenu 
                    steps={steps}
                    title={mode === SetPointAdminMode.Add ? 'New Alarm Setpoint': setpointFormData.setPointName}
                    activeStep={activeStep}
                    cancelButtonState={cancelButtonState}
                    prevButtonState={prevButtonState}
                    nextButtonState={nextButtonState}
                    saveChangesButtonState={saveChangesButtonState}
                    onCancel={onCancelEdit}
                    onSaveChanges={handleSubmit(() => onSaveChanges())}
                    onCancelStep={onCancelStep}
                    onStepChange={onStepChange}
                    onStepClick={step => setActiveStep(step.key)}
                    viewOptionState={viewOptionState}
                    setViewOptionState={setViewOptionState}
                    onToggleOptionsAction={activeStep === StepEnum.Notifications ? () => setOpenSideSheet(!openSideSheet) : undefined}
                    actions={activeStep === StepEnum.Usage ? 
                            usageActionItems : 
                            activeStep === StepEnum.Notifications && 
                                setpointNotificationState.state.notificationType === NotificationTypeEnum.OneTime && 
                                setpointNotificationState.state.activeTab === AlarmSetpointNotificationTabEnum.Recipients ?
                                recipientActionItems :
                            activeStep === StepEnum.Notifications && 
                                setpointNotificationState.state.notificationType === NotificationTypeEnum.OneTime && 
                                setpointNotificationState.state.activeTab === AlarmSetpointNotificationTabEnum.NotificationLists ?
                                notificationListActionItems :
                            activeStep === StepEnum.Actions ? 
                            [] : 
                            undefined
                    }
                    onExecuteAction={onExecuteAction}
                >
                    {activeStep === StepEnum.Logic &&
                        <AlarmSetpointProperties groupID={groupID} />
                    }

                    {activeStep === StepEnum.Usage &&
                        <AlarmSetpointUsage
                            ref={setpointUsageRef}
                            usageObjects={usageObjects}
                            openSideSheet={openSideSheet}
                            onCloseSideSheet={() => setOpenSideSheet(false)}
                            setViewOptionState={setViewOptionState}
                            setpointUsageState={setpointUsageState}
                        />
                    }

                    {activeStep === StepEnum.Notifications &&
                        <AlarmSetpointNotifications
                            ref={setpointNotificationsRef}
                            groupID={groupID}
                            openSideSheet={openSideSheet}  
                            onCloseSideSheet={() => setOpenSideSheet(false)}
                            setViewOptionState={setViewOptionState} 
                            setpointNotificationState={setpointNotificationState}
                        />

                    }

                    {activeStep === StepEnum.Actions &&
                            <AlarmSetpointMessaging 
                            groupID={groupID} 
                            openSideSheet={openSideSheet} 
                            onCloseSideSheet={() => setOpenSideSheet(false)}
                            setViewOptionState={setViewOptionState} 
                            setpointMessagingState={setpointMessagingState}                            

                        />
                 
                    }

                    {activeStep === StepEnum.Summary &&
                        <AlarmSetpointSummary groupID={groupID} />
                    }

                </MobileStepMenu>
                }


                {!isPhone && <StepMenu
                    landing={landing}
                    steps={steps}
                    title={mode === SetPointAdminMode.Add ? 'New Alarm Setpoint': setpointFormData.setPointName}
                    nonLinear={true}
                    activeStep={activeStep}
                    cancelButtonState={cancelButtonState}
                    prevButtonState={prevButtonState}
                    nextButtonState={nextButtonState}
                    saveChangesButtonState={saveChangesButtonState}
                    onCancel={onCancelEdit}
                    //onSaveAsNew={handleSubmit(() => {} /*onSaveAsNew()*/)}
                    onSaveChanges={handleSubmit(() => onSaveChanges())}
                    onCancelStep={onCancelStep}
                    onStepChange={onStepChange}
                    onStepClick={step => setActiveStep(step.key)}
                >
                    {activeStep === StepEnum.Logic &&
                        <AlarmSetpointProperties 
                            groupID={groupID}
                            tagID={tagID}
                        />
                    }

                    {activeStep === StepEnum.Usage &&
                        <AlarmSetpointUsage 
                            usageObjects={usageObjects}
                            openSideSheet={openSideSheet} 
                            setpointUsageState={setpointUsageState}
                        />
                    }

                    {activeStep === StepEnum.Notifications &&
                        <AlarmSetpointNotifications
                            groupID={groupID} 
                            openSideSheet={openSideSheet} 
                            setpointNotificationState={setpointNotificationState}                            
                            />
                    }

                    {activeStep === StepEnum.Actions &&
                        <AlarmSetpointMessaging 
                            groupID={groupID} 
                            openSideSheet={openSideSheet} 
                            setpointMessagingState={setpointMessagingState}                            

                        />
                    }

                    {activeStep === StepEnum.Summary &&
                        <AlarmSetpointSummary groupID={groupID}/>
                    }

                </StepMenu>
                }
            </Form>
            }


            <UnsavedChangesPrompt
                hasChanges={hasChanges}
                getUserConfirmation={getUserExitConfirmation}
            />
        </LoadingZone>
    );
}
